import { HttpResponse } from "@angular/common/http";
import { Component, DestroyRef, OnInit, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  UrlSegment,
} from "@angular/router";
import { Observable, forkJoin, take } from "rxjs";
import { OrganizationService } from "src/app/shared/services/rnapi2-service/apis/organization.service";
import { UserLoginState } from "../../../../../core/models/user.login.state";
import { LoggedInInfoService } from "../../../../services/loggedInInfo/logged-in-info.service";
import { NavigationService } from "../../../../services/navigation/navigation.service";
import {
  RnCommonId,
  RnCommonSearch,
  RnOrganizationsProfileVM,
  RnOrganizationsProfileVMRNResponseRelay,
  RnWLPInformationVM,
} from "../../../../services/rnapi2-service/models/models";
import { OrganizationCacheService } from "src/app/shared/services/organization-cache/organization-cache.service";
import { FeatureFlagService } from "src/app/shared/services/feature-flag/feature-flag.service";
import { EnvService } from "src/app/core/services/env.service";
import { ConstantsService } from "src/app/shared/services/constants/constants.service";

declare let pendo: any;

@Component({
    selector: "app-rn-navigation-bar",
    templateUrl: "./rn-navigation-bar.component.html",
    styleUrls: ["./rn-navigation-bar.component.scss"],
    standalone: false
})
export class RnNavigationBarComponent implements OnInit {
  private loggedInInfoService = inject(LoggedInInfoService);
  private navigationService = inject(NavigationService);
  private organizationService = inject(OrganizationService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private organizationCacheService = inject(OrganizationCacheService);
  private featureFlagService = inject(FeatureFlagService);
  private constantsService = inject(ConstantsService);
  private readonly destroyRef = inject(DestroyRef);

  public showRNSupportItems: boolean;
  public internalLinkIsVisible: boolean;
  public wlpOrgDescription: string;
  public wlpGettingStartedWebsite: string;
  public wlpOrgs: RnWLPInformationVM[];
  public isSendFeedbackShown: boolean;
  public orgProfile: RnOrganizationsProfileVM;
  public isAppHubContext = false;
  public isAdminContext = false;
  public showAdminConsole = false;
  public showVisitConnect = false;
  public connectUrl = "";
  public securityHubUrl = "";
  public customerType: string;
  public isAzure = false;
  public isSolutionHubEnabled = false;
  public solutionHubUrl = "";
  public communityUrl = "";
  public newFileManagerUrl = "";
  public isNewFileManagerEnabled = false;
  public isNewFileManagerBetaEnabled = false;
  public hasEntryTierPackage = false;
  public loadingFeatureFlag = false;

  ngOnInit(): void {
    this.loggedInInfoService.LoggedInUser.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((u) => {
      this.loggedInUser = u;
      if(u) {
        this.initializeForUser();
      }
    });

    this.activatedRoute.url.subscribe((u) => {
      this.currentUrl = u;
    });

    this.loggedInInfoService.LoggedInUser.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((u) => {
      if (u) {
        this.internalLinkIsVisible = u.IsInternal();
        this.wlpOrgDescription = u.OrganizationWLPDescription;
        this.isAzure = u.OrgDataCenterTypeName === "Azure";
      }
    });

    this.loggedInInfoService.LoggedInuserOrgConfig.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((c) => {
      if (c) {
        this.showRNSupportItems = !c.DisallowRNSupport;
      }
    });

    this.loggedInInfoService.WLPInfo.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((w) => {
      if (w) {
        this.wlpOrgs = [];
        w.forEach((org) => {
          if (
            org.InternalSecurityGroups.length > 0 &&
            this.loggedInInfoService.loggedInUserHasRights(
              org.InternalSecurityGroups,
            )
          ) {
            this.wlpOrgs.push(org);
          }
        });
      }
    });

    this.loggedInInfoService.WLPContactInfo.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((c) => {
      if (c) {
        this.wlpGettingStartedWebsite = c.GettingStartedWebsite;
      }
    });
    
    this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        this.setContexts();
      }
    });      
  }

  initializeForUser(): void {
    const orgID = this.loggedInUser?.OrganizationID?.toString();  
    this.loadOrganizationData(orgID).subscribe({
      next: (r) => {
        this.orgProfile = r.body.data;
        this.initializeSolutionHubLink();
      },
      error: (err) => {
        console.error("Error loading organization profile:", err);
      },
      complete: () => {
        //ensures that the load completes before executing remaining code. This way, the "Visit Connect" sidebar loads only after the customer type is found:
        this.customerType = this.orgProfile.CustomerType;
        this.showVisitConnect =
          this.loggedInUser &&
          this.loggedInInfoService.loggedInUserHasRights(["MGINSIGHT"]) &&
          this.customerType == "Rightworks";
        this.sendFeedbackLogic();
      }
    });
       
    const payload: RnCommonSearch = {
      search: "rn_connect_url",
    };
    this.organizationService
      .apiV2OrganizationsGetconfigurablevaluePost(payload)
      .subscribe((r) => {
        this.connectUrl = r.data;
      });

    this.showAdminConsole =
      this.loggedInUser &&
      this.loggedInInfoService.loggedInUserHasRights(["VIEWORGDET"]);

    this.setContexts();

    const envPrefix = EnvService.EnvVariables().envPrefix.toString();
    this.securityHubUrl = this.constantsService.buildSpaUrl(envPrefix, this.constantsService.securityHubUrlFragment, false);
    this.initializeCommunityLink();
    this.initializeFileManagerLink();
    if (this.loggedInUser?.AssignedPackage === "Rightworks QuickBooks Hosting") {
      this.hasEntryTierPackage = true;
    }
  }

  isEntryTier() {
    return this.hasEntryTierPackage;
  }

  initializeSolutionHubLink(): void {
    // Only show for direct customers 
    if(this.orgProfile?.CustomerType?.toLowerCase() !== "rightworks") {
      return;
    }

    const envPrefix = EnvService.EnvVariables().envPrefix.toString();
    this.solutionHubUrl = this.constantsService.buildSpaUrl(envPrefix, this.constantsService.solutionHubUrlFragment, false);
    this.loadingFeatureFlag = true;
    this.featureFlagService.getFlag('enable-solution-hub', false).subscribe((isEnabled: boolean) => {
      this.isSolutionHubEnabled = isEnabled;
      this.loadingFeatureFlag = false;
    });
  }

  initializeFileManagerLink(): void {
    this.newFileManagerUrl = this.constantsService.newFileManagerUrl;
    
    this.featureFlagService.getFlag('enable-file-manager-beta-link', false).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isEnabled: boolean) => {
      this.isNewFileManagerBetaEnabled = isEnabled;
    });
    this.featureFlagService.getFlag('enable-new-file-manger-link', false).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isEnabled: boolean) => {
      this.isNewFileManagerEnabled = isEnabled;
    });
  }

  initializeCommunityLink(): void {
    const smbSegmentType = "SMB";
    const allowedTypes = ["Account Owner", "Account Admin", "User"];
    const userType = this.loggedInUser?.UserType?.toLowerCase();
    const allowedTypesLower = allowedTypes.map(type => type.toLowerCase());
    if (!this.loggedInUser || !allowedTypesLower.includes(userType)) {
      return;
    }

    const payload: RnCommonSearch = { search: "community_url" };
    const orgIdPayload = new RnCommonId();
    orgIdPayload.Id = this.loggedInUser.organizationid;
    const forkJoin$ = forkJoin({      
      organizationConfig: this.organizationService.apiV2OrganizationsGetorganizationconfigurationPost(orgIdPayload, "response"),
      communityUrlResult: this.organizationService.apiV2OrganizationsGetconfigurablevaluePost(payload)
    });
    
    forkJoin$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({      
      next: ({ organizationConfig, communityUrlResult }) => {
        const customerSegment = organizationConfig.body?.data?.CustomerSegment?.toLowerCase();
        if (customerSegment !== smbSegmentType.toLowerCase()) {
          this.communityUrl = communityUrlResult?.data;
        }
      },
      error: (err) => {
        console.error("Error fetching Organization Profile or Community URL: ", err);
      }
    });     
  }
  
  setContexts(): void {
    this.isAppHubContext =
      window.location.href.includes(
        "user/" + this.loggedInUser?.UserID?.toString(),
      );
    this.isAdminContext =
      window.location.href.includes(
        "organization/" + this.loggedInUser?.OrganizationID?.toString(),
      ) ||
      (window.location.href.includes("user/") && !this.isAppHubContext);
  }

  sendFeedbackLogic(): void {
    const payload = new RnCommonSearch();
    if (this.orgProfile?.CustomerType) {
      payload.search = "show_sendfeedback";
      this.organizationService
        .apiV2OrganizationsGetconfigurablevaluePost(payload, "response")
        .subscribe((r) => {
          if (r.body.data && r.body.data !== "") {
            const allowedList = r.body.data;
            this.isSendFeedbackShown =
              allowedList.split(",").includes(this.customerType);
          }
        });
    }
  }

  async goToAppHubUsersPage(): Promise<void> {
    if (this.loggedInUser && this.loggedInUser.ProfileCreated) {
      await this.navigationService.OpenUser(
        this.loggedInUser.UserID,
        this.loggedInUser.OrganizationID,
      );
    }
  }

  openFileManager(): void {
    if (this.isNewFileManagerEnabled || this.isNewFileManagerBetaEnabled) {
      window.open(this.newFileManagerUrl, "_blank", "noreferrer=true, noopener=true");
    }
    else {
      window.open("https://filemanager.rightnetworks.com", "_blank", "noreferrer=true, noopener=true");
    }
  }

  async goToAdminConsole(): Promise<void> {
    if (this.loggedInUser && this.loggedInUser.ProfileCreated) {
      await this.navigationService.OpenOrganization(
        this.loggedInUser.OrganizationID,
      );
    }
  }

  goToConnect(): void {
    window.open(this.connectUrl, "_blank");
  }

  goToSecurityHub(): void {
    window.open(this.securityHubUrl, "_blank");
  }

  goToSolutionHub(): void {
    window.open(this.solutionHubUrl, "_blank");
  }

  goToCommunity(): void {
    if (this.communityUrl) {
      window.open(this.communityUrl, "_blank");
    } else {
      console.error("Community URL is not defined.");
    }
  }

  loadOrganizationData(
    orgId: string,
  ): Observable<HttpResponse<RnOrganizationsProfileVMRNResponseRelay>> {
    return this.organizationCacheService.getOrganizationProfile(orgId);
  }

  showHomeLink(): boolean {
    return this.loggedInUser.IsInternal();
  }
  async goHome(): Promise<void> {
    await this.navigationService.GoHome();
  }

  public launchDownloadSetupWizard(): void {
    this.navigationService.showDownloadSetupDialog(false);
  }

  public async openInternalWLPOrg(wlpOrgID: number): Promise<void> {
    await this.navigationService.OpenOrganization(wlpOrgID);
  }

  private loggedInUser: UserLoginState;
  private currentUrl: UrlSegment[];
}
